import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Sun, Moon, PenNib, SignOut, List, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function TopNav() {
  const { user, logout } = useAuth();
  const { mode, toggle } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setIsOpen(false);
  };

  const navLinks = [
    { to: "/", label: "Home", end: true },
    { to: "/explore", label: "Explore" },
    { to: "/series", label: "Series" },
    { to: "/articles", label: "Articles" },
    { to: "/authors", label: "Authors" },
    ...(user?.id ? [{ to: "/dashboard", label: "Dashboard" }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group z-50" onClick={() => setIsOpen(false)}>
          <PenNib size={22} weight="duotone" className="text-foreground" />
          <span className="font-serif text-2xl font-black tracking-tight">Inkspot</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => `link-underline ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="h-9 w-9 grid place-items-center border border-border hover:bg-secondary transition-colors"
            aria-label="Toggle theme"
          >
            {mode === "dark" ? <Sun size={16} weight="duotone" /> : <Moon size={16} weight="duotone" />}
          </button>

          <div className="hidden md:flex items-center gap-3">
            {user && user.id ? (
              <>
                <Button asChild variant="outline" className="rounded-none border-foreground">
                  <Link to="/dashboard/new"><PenNib size={14} className="mr-1.5" /> Write</Link>
                </Button>
                <Button variant="ghost" onClick={handleLogout} className="rounded-none">
                  <SignOut size={16} />
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" className="rounded-none">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild className="rounded-none bg-foreground text-background hover:bg-foreground/90">
                  <Link to="/register">Start writing</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden h-9 w-9 grid place-items-center border border-border hover:bg-secondary transition-colors z-50"
          >
            {isOpen ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-background md:hidden">
          <div className="flex flex-col items-center justify-center h-full gap-8 px-6 text-center">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                onClick={() => setIsOpen(false)}
                className="font-serif text-4xl font-black tracking-tight hover:italic transition-all"
              >
                {link.label}
              </NavLink>
            ))}
            <div className="w-full h-px bg-border my-4" />
            {user && user.id ? (
              <div className="flex flex-col gap-4 w-full max-w-xs">
                <Button asChild className="rounded-none h-14 text-lg bg-foreground text-background" onClick={() => setIsOpen(false)}>
                  <Link to="/dashboard/new">Write an essay</Link>
                </Button>
                <Button variant="outline" className="rounded-none h-14 text-lg border-foreground" onClick={handleLogout}>
                  Sign out
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-4 w-full max-w-xs">
                <Button asChild className="rounded-none h-14 text-lg bg-foreground text-background" onClick={() => setIsOpen(false)}>
                  <Link to="/register">Start writing</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-none h-14 text-lg border-foreground" onClick={() => setIsOpen(false)}>
                  <Link to="/login">Sign in</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
